import { Button, Stack, Typography } from '@mui/material'
import { CargoType, CarrierVisitStatus, HandleOpenOrdersOption } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { RemoveOrFinishOpenOrders } from '@planning/pages/Order'
import { useVesselMessagesHandler } from '@planning/pages/Vessel/hooks/useVesselMessagesHandler'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { orderService, vesselVisitService } from '@planning/services'
import { formatDateTime } from '@planning/utils'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { FC, useCallback } from 'react'
import { VisitDataHeaderCard } from '../../atoms/VisitDataHeaderCard'
import { CloseVisitDialog } from '../../molecules/CloseVisitDialog'
import { VisitTimingDialog } from '../../molecules/VisitTimingDialog'

type VisitTimingsProps = {
  vesselVisitItem: IVesselVisitItem
  isEditDisabled?: boolean
}

export const VisitTimings: FC<VisitTimingsProps> = observer(props => {
  const { vesselVisitItem, isEditDisabled = false } = props
  const { tenantStore, dialogStore, appViewStore } = usePlanningStore()
  const { getTimestampExceptionMessage } = useVesselMessagesHandler()

  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const hasAta = !!vesselVisitItem?.data.ata
  const hasAtb = !!vesselVisitItem?.data.atb
  const hasAtd = !!vesselVisitItem?.data.atd
  const hasAto = !!vesselVisitItem?.data.operationStart
  const hasAtc = !!vesselVisitItem?.data.operationEnd

  const isVisitClosed = vesselVisitItem?.data.status === CarrierVisitStatus.Closed
  const isVisitCancelled = vesselVisitItem?.data.status === CarrierVisitStatus.Cancelled
  const isGeneralCargoVisit = vesselVisitItem?.data.cargoType === CargoType.GeneralCargo

  const handleTimeStampChange = async (
    key: 'ata' | 'atb' | 'atd' | 'operationStart' | 'operationEnd',
    value: string | null | undefined,
    initialValue?: string | null,
  ) => {
    let timeStampName = key.toUpperCase()

    if (key === 'operationStart') {
      timeStampName = 'ATO'
    } else if (key === 'operationEnd') {
      timeStampName = 'ATC'
    }

    const hasInitialValue = !!initialValue

    try {
      await vesselVisitService.put({
        ...vesselVisitItem.data,
        [key]: value,
        vesselIds: vesselVisitItem.data.carrierIds,
      })

      appViewStore.setShowAlert(
        'success',
        t('timestampUpdated', '{name} was successfully {operation}.', {
          name: timeStampName,
          operation: hasInitialValue ? t('updated', 'updated') : t('created', 'created'),
        }),
      )
    } catch (error: any) {
      const timestampExceptionMessage = getTimestampExceptionMessage(error)
      if (timestampExceptionMessage) {
        appViewStore.setShowAlert('error', timestampExceptionMessage)
      } else {
        appViewStore.setShowAlert(
          'error',
          t(
            'failedToUpdateTimeStamp',
            '{name} could not be updated. Please ensure the selected date is after the previous timestamp',
            { name: timeStampName },
          ),
        )
      }
    }
  }

  const openSetAtaModal = useCallback(() => {
    const initialAta = vesselVisitItem?.data.ata ?? vesselVisitItem?.data.eta
    dialogStore.openDialog(
      <VisitTimingDialog
        initialValues={{ date: dayjs(initialAta) }}
        timingType='ATA'
        onSubmit={async value => handleTimeStampChange('ata', value, vesselVisitItem?.data.ata)}
      />,
      false,
      {
        open: true,
        maxWidth: 'xs',
        slotProps: {},
      },
    )
  }, [dialogStore, vesselVisitItem.data])

  const openSetAtbModal = useCallback(() => {
    const initialAtb = vesselVisitItem?.data.atb ?? vesselVisitItem?.data.etb
    dialogStore.openDialog(
      <VisitTimingDialog
        initialValues={{ date: dayjs(initialAtb) }}
        timingType='ATB'
        onSubmit={async value => handleTimeStampChange('atb', value, vesselVisitItem?.data.atb)}
      />,
      false,
      {
        open: true,
        maxWidth: 'xs',
        slotProps: {},
      },
    )
  }, [dialogStore, vesselVisitItem.data])

  const openSetAtdModal = useCallback(() => {
    const intialAtd = vesselVisitItem?.data.atd ?? vesselVisitItem?.data.etd
    dialogStore.openDialog(
      <VisitTimingDialog
        initialValues={{ date: dayjs(intialAtd) }}
        timingType='ATD'
        onSubmit={async value => handleTimeStampChange('atd', value, vesselVisitItem?.data.atd)}
      />,
      false,
      {
        open: true,
        maxWidth: 'xs',
        slotProps: {},
      },
    )
  }, [dialogStore, vesselVisitItem.data])

  const openSetAtoModal = useCallback(() => {
    dialogStore.openDialog(
      <VisitTimingDialog
        initialValues={{ date: dayjs(vesselVisitItem?.data.operationStart) }}
        timingType='ATO'
        onSubmit={async value =>
          handleTimeStampChange('operationStart', value, vesselVisitItem?.data.operationStart)
        }
      />,
      false,
      {
        open: true,
        maxWidth: 'xs',
        slotProps: {},
      },
    )
  }, [dialogStore, vesselVisitItem.data])

  const openSetAtcModal = useCallback(() => {
    dialogStore.openDialog(
      <VisitTimingDialog
        initialValues={{ date: dayjs(vesselVisitItem?.data.operationEnd) }}
        timingType='ATC'
        onSubmit={async value =>
          handleTimeStampChange('operationEnd', value, vesselVisitItem?.data.operationEnd)
        }
      />,
      false,
      {
        open: true,
        maxWidth: 'xs',
        slotProps: {},
      },
    )
  }, [dialogStore, vesselVisitItem.data])

  const openCloseVisitDialog = useCallback(() => {
    dialogStore.openDialog(
      <CloseVisitDialog
        onSubmit={async () => {
          await vesselVisitService.updateVesselVisitStatus(
            vesselVisitItem.data.id,
            CarrierVisitStatus.Closed,
          )
          appViewStore.setShowAlert('success', t('visitClosed', 'Visit closed successfully'))
          dialogStore.close()
        }}
      />,
      false,
      {
        open: true,
        maxWidth: 'xs',
        slotProps: {},
      },
    )
  }, [dialogStore, vesselVisitItem.data, appViewStore, t])

  const AtaContainer: FC<{ isDisabled?: boolean }> = ({ isDisabled }) => (
    <VisitDataHeaderCard
      width='185px'
      title={t('ata', 'ATA')}
      onClick={() => openSetAtaModal()}
      disabled={isVisitClosed || isVisitCancelled || isDisabled}
    >
      <Typography variant='body1'>{formatDateTime(vesselVisitItem?.data.ata)}</Typography>
    </VisitDataHeaderCard>
  )

  const AtbContainer: FC<{ isDisabled?: boolean }> = ({ isDisabled }) => (
    <VisitDataHeaderCard
      width='185px'
      title={t('atb', 'ATB')}
      onClick={() => openSetAtbModal()}
      disabled={isVisitClosed || isVisitCancelled || isDisabled}
    >
      <Typography variant='body1'>{formatDateTime(vesselVisitItem?.data.atb)}</Typography>
    </VisitDataHeaderCard>
  )

  const AtdContainer: FC<{ isDisabled?: boolean }> = ({ isDisabled }) => (
    <VisitDataHeaderCard
      width='185px'
      title={t('atd', 'ATD')}
      onClick={() => openSetAtdModal()}
      disabled={isVisitClosed || isVisitCancelled || isDisabled}
    >
      <Typography variant='body1'>{formatDateTime(vesselVisitItem?.data.atd)}</Typography>
    </VisitDataHeaderCard>
  )

  const AtoContainer: FC<{ isDisabled?: boolean }> = ({ isDisabled }) => (
    <VisitDataHeaderCard
      width='185px'
      title={t('ato', 'ATO')}
      onClick={() => openSetAtoModal()}
      disabled={isVisitClosed || isVisitCancelled || isDisabled}
    >
      <Typography variant='body1'>
        {formatDateTime(vesselVisitItem?.data.operationStart)}
      </Typography>
    </VisitDataHeaderCard>
  )

  const AtcContainer: FC<{ isDisabled?: boolean }> = ({ isDisabled }) => (
    <VisitDataHeaderCard
      width='185px'
      title={t('atc', 'ATC')}
      onClick={() => openSetAtcModal()}
      disabled={isVisitClosed || isVisitCancelled || isDisabled}
    >
      <Typography variant='body1'>{formatDateTime(vesselVisitItem?.data.operationEnd)}</Typography>
    </VisitDataHeaderCard>
  )

  if (isEditDisabled) {
    return (
      <>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={theme.customSpacing.s}
          justifyContent={'flex-start'}
        >
          {hasAta ? (
            <AtaContainer isDisabled />
          ) : (
            <VisitDataHeaderCard width='185px' title={t('eta', 'ETA')} disabled={true}>
              <Typography variant='body1'>
                {vesselVisitItem?.data.eta ? formatDateTime(vesselVisitItem.data.eta) : '-'}
              </Typography>
            </VisitDataHeaderCard>
          )}
          {!tenantStore.skipBerthTimestamp && hasAtb ? (
            <AtbContainer isDisabled />
          ) : (
            <VisitDataHeaderCard width='185px' title={t('etb', 'ETB')} disabled={true}>
              <Typography variant='body1'>
                {vesselVisitItem?.data.etb ? formatDateTime(vesselVisitItem.data.etb) : '-'}
              </Typography>
            </VisitDataHeaderCard>
          )}
          {hasAto && <AtoContainer isDisabled />}
          {hasAtc && <AtcContainer isDisabled />}
          {hasAtd && <AtdContainer isDisabled />}
        </Stack>
      </>
    )
  }

  if (!hasAta) {
    return (
      <>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={theme.customSpacing.s}
          justifyContent={'flex-start'}
        >
          <VisitDataHeaderCard width='185px' title={t('eta', 'ETA')} disabled={true}>
            <Typography variant='body1'>
              {vesselVisitItem?.data.eta ? formatDateTime(vesselVisitItem.data.eta) : '-'}
            </Typography>
          </VisitDataHeaderCard>
        </Stack>
        {
          <Button
            variant='contained'
            color='inherit'
            onClick={() => openSetAtaModal()}
            sx={{ width: '130px' }}
            data-cy='set-ata-btn'
            disabled={isVisitCancelled}
          >
            {t('setAta', 'Set ATA')}
          </Button>
        }
      </>
    )
  }

  if (hasAta && !hasAtb && !tenantStore.skipBerthTimestamp) {
    return (
      <>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={theme.customSpacing.s}
          justifyContent={'flex-start'}
        >
          <AtaContainer />
          <VisitDataHeaderCard width='185px' title={t('etb', 'ETB')} disabled={true}>
            <Typography variant='body1'>
              {vesselVisitItem?.data.etb ? formatDateTime(vesselVisitItem.data.etb) : '-'}
            </Typography>
          </VisitDataHeaderCard>
        </Stack>
        <Button
          variant='contained'
          color='inherit'
          onClick={() => openSetAtbModal()}
          sx={{ width: '130px' }}
          data-cy='set-atb-btn'
          disabled={isVisitCancelled}
        >
          {t('setAtb', 'Set ATB')}
        </Button>
      </>
    )
  }

  if (hasAta && (hasAtb || tenantStore.skipBerthTimestamp) && !hasAto) {
    return (
      <>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={theme.customSpacing.s}
          justifyContent={'flex-start'}
        >
          <AtaContainer isDisabled />
          {!tenantStore.skipBerthTimestamp && <AtbContainer />}
          <VisitDataHeaderCard width='185px' title={t('etd', 'ETD')} disabled={true}>
            <Typography variant='body1'>
              {vesselVisitItem?.data.etd ? formatDateTime(vesselVisitItem.data.etd) : '-'}
            </Typography>
          </VisitDataHeaderCard>
        </Stack>
        <Button
          variant='contained'
          color='inherit'
          onClick={async () => {
            try {
              await vesselVisitService.startOperations(vesselVisitItem.id)
              appViewStore.setShowAlert(
                'success',
                t('operationStarted', 'Operations were successfully started.'),
              )
            } catch (error: any) {
              const visitOnTerminalMessage = getTimestampExceptionMessage(error)
              if (visitOnTerminalMessage) {
                appViewStore.setShowAlert('error', visitOnTerminalMessage)
              } else {
                appViewStore.setShowAlert(
                  'error',
                  error?.response?.data?.detail ?? 'Failed to start operations',
                )
              }
            }
          }}
          sx={{ width: '130px' }}
          data-cy='set-ato-btn'
          disabled={isVisitCancelled}
        >
          {t('startOperation', 'Start operation')}
        </Button>
      </>
    )
  }

  if (hasAta && (hasAtb || tenantStore.skipBerthTimestamp) && hasAto && !hasAtc) {
    return (
      <>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={theme.customSpacing.s}
          justifyContent={'flex-start'}
        >
          <AtaContainer isDisabled />
          {!tenantStore.skipBerthTimestamp && <AtbContainer isDisabled />}
          <AtoContainer />
          <VisitDataHeaderCard width='185px' title={t('etd', 'ETD')} disabled={true}>
            <Typography variant='body1'>
              {vesselVisitItem?.data.etd ? formatDateTime(vesselVisitItem?.data.etd) : '-'}
            </Typography>
          </VisitDataHeaderCard>
        </Stack>
        <Button
          variant='contained'
          color='inherit'
          sx={{ width: '130px' }}
          onClick={async () => {
            if (vesselVisitItem?.hasOpenOrders && !isGeneralCargoVisit) {
              dialogStore.openDialog(
                <RemoveOrFinishOpenOrders
                  visitItem={vesselVisitItem}
                  callback={async (
                    handleOpenInboundOrdersOption,
                    handleOpenOutboundOrdersOption,
                    handleOpenRestowOrdersOption,
                  ) =>
                    await vesselVisitService.endOperations(
                      vesselVisitItem.id,
                      handleOpenInboundOrdersOption,
                      handleOpenOutboundOrdersOption,
                      handleOpenRestowOrdersOption,
                    )
                  }
                />,
                false,
                {
                  open: true,
                  maxWidth: 'lg',
                  slotProps: {},
                },
              )
              return
            }
            try {
              if (isGeneralCargoVisit) {
                if (vesselVisitItem?.hasOpenOrders) {
                  await orderService.removeOrFinishOpenOrders({
                    carrierVisitId: vesselVisitItem.id,
                    inboundRemoveOrFinishOrderOption: HandleOpenOrdersOption.Finish,
                    outboundRemoveOrFinishOrderOption: HandleOpenOrdersOption.Finish,
                    restowRemoveOrFinishOrderOption: HandleOpenOrdersOption.Finish,
                  })
                }

                await vesselVisitService.endOperations(
                  vesselVisitItem.id,
                  HandleOpenOrdersOption.Finish,
                  HandleOpenOrdersOption.Finish,
                  HandleOpenOrdersOption.Finish,
                )
              } else {
                await vesselVisitService.endOperations(
                  vesselVisitItem.id,
                  HandleOpenOrdersOption.Remove,
                  HandleOpenOrdersOption.Remove,
                  HandleOpenOrdersOption.Remove,
                )
              }

              appViewStore.setShowAlert(
                'success',
                t('operationEnded', 'Operations were successfully ended.'),
              )
            } catch (error: any) {
              appViewStore.setShowAlert(
                'error',
                error?.response?.data?.detail ?? 'Failed to start operations',
              )
            }
          }}
          data-cy='set-atc-btn'
          disabled={isVisitCancelled}
        >
          {t('endOperation', 'End operation')}
        </Button>
      </>
    )
  }

  if (hasAta && (hasAtb || tenantStore.skipBerthTimestamp) && hasAto && hasAtc && !hasAtd) {
    return (
      <>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={theme.customSpacing.s}
          justifyContent={'flex-start'}
        >
          <AtaContainer isDisabled />
          {!tenantStore.skipBerthTimestamp && <AtbContainer isDisabled />}
          <AtoContainer isDisabled />
          <AtcContainer />
          <VisitDataHeaderCard width='185px' title={t('etd', 'ETD')} disabled={true}>
            <Typography variant='body1'>
              {vesselVisitItem?.data.etd ? formatDateTime(vesselVisitItem.data.etd) : '-'}
            </Typography>
          </VisitDataHeaderCard>
        </Stack>
        <Button
          variant='contained'
          color='inherit'
          onClick={() => openSetAtdModal()}
          sx={{ width: '130px' }}
          data-cy='set-atd-btn'
          disabled={isVisitCancelled}
        >
          {t('setAtd', 'Set ATD')}
        </Button>
      </>
    )
  }

  if (hasAta && hasAto && hasAtc && hasAtd) {
    return (
      <>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={theme.customSpacing.s}
          justifyContent={'flex-start'}
        >
          <AtaContainer isDisabled />
          {!tenantStore.skipBerthTimestamp && <AtbContainer isDisabled />}
          <AtoContainer isDisabled />
          <AtcContainer isDisabled />
          <AtdContainer />
        </Stack>
        {!isVisitClosed && (
          <Stack direction={'row'}>
            <Button
              variant='contained'
              color='inherit'
              onClick={() => openCloseVisitDialog()}
              sx={{ width: '130px' }}
              data-cy='close-visit-btn'
              disabled={isVisitCancelled}
            >
              {t('closeVisit', 'Close visit')}
            </Button>
          </Stack>
        )}
      </>
    )
  }
})
